import style from "./Style.module.scss";
import React, { useState } from "react";
import Modal from "react-modal";
import Scrollbar from "react-scrollbars-custom";
import { useQuery } from "@tanstack/react-query";
import { getRewardToFcoHistoryOptions, getMatchPredictionsOptions, getStateOptions } from "@/client/@tanstack/react-query.gen";
import dayjs from "dayjs";
import { getConfig } from "@/models/Config";
import { getTeamCode } from "@/utils/teamCode";

Modal.setAppElement("#root");

type Props = {
  modalStatus: boolean;
  closeModal: () => void;
};

const statusMap = {
  pending: "Chờ xử lý",
  success: "Thành công",
  failed: "Thất bại",
} as const;

type TabType = "rewards" | "predictions" | "specials";

const ModalHistory: React.FC<Props> = ({ modalStatus, closeModal }) => {
  const [tab, setTab] = useState<TabType>("rewards");

  const settings = getConfig();

  const { data: historyData, isLoading: loadingRewards } = useQuery(getRewardToFcoHistoryOptions());
  const histories = historyData?.rewardToFcoHistory ?? [];
  const { data: matchPredictions, isLoading: loadingPredicts } = useQuery(getMatchPredictionsOptions());
  const { data: userState, isLoading: loadingState } = useQuery(getStateOptions());

  const rewards = settings?.rewards ?? [];
  const matches = settings?.matches ?? [];

  const rewardsMap = new Map(rewards.map((r) => [r?.id, r]));
  const matchesMap = new Map(matches.map((m) => [m?.id, m]));

  const hisPredicts = (matchPredictions?.matchPredictions ?? [])
    .map((predict) => ({
      ...predict,
      match: matchesMap.get(predict?.matchId),
    }))
    .sort((a, b) => {
      const timeA = a?.settledAt ? dayjs(a.settledAt).valueOf() : 0;
      const timeB = b?.settledAt ? dayjs(b.settledAt).valueOf() : 0;
      return timeB - timeA;
    });

  const players = settings?.players ?? [];

  const predictionStatusText = (
    p?: { status: "pending" | "settled"; rewardPoint: number } | null,
  ): string => {
    if (!p) return "";
    return p.status === "settled"
      ? p.rewardPoint > 0
        ? `Dự đoán đúng`
        : "Dự đoán sai"
      : "Đang chờ kết quả";
  };

  const championPrediction = userState?.championPrediction ?? null;
  const topScorerPrediction = userState?.topScorerPrediction ?? null;

  const otherPredicts = [
    ...(championPrediction
      ? [{
        type: "Đội Vô địch",
        name: championPrediction.team,
        status: championPrediction.status,
        statusText: predictionStatusText(championPrediction),
        rewardPoint: championPrediction.rewardPoint,
        createdAt: championPrediction.createdAt,
      }]
      : []),
    ...(topScorerPrediction
      ? [{
        type: "Vua phá lưới",
        name: players.find((p) => p.id === topScorerPrediction.playerId)?.name ?? "",
        status: topScorerPrediction.status,
        statusText: predictionStatusText(topScorerPrediction),
        rewardPoint: topScorerPrediction.rewardPoint,
        createdAt: topScorerPrediction.createdAt,
      }]
      : []),
  ];

  const selfClose = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const modalElement = document.querySelector(".swing-in-top-fwd");
    if (modalElement) {
      modalElement.classList.add("swing-out-top-bck");
    }
    setTimeout(() => closeModal(), 300);
  };

  const getTextUsage = (source: string | undefined): string => {
    if (source === "spin") return "Quà quay";
    if (source === "accumulated_spun") return "Tích luỹ lượt quay";
    if (source === "milestone_server") return "Mốc quay toàn server";
    if (source === "giftcode") return "Nhập Giftcode";
    if (source === "point_reward") return "Shop Đổi điểm";
    if (source === "mini_spin") return "Mini spin";
    if (source === "leaderboard") return "Bảng xếp hạng";
    return "";
  };

  return (
    <Modal
      isOpen={modalStatus}
      onRequestClose={selfClose}
      contentLabel="Lịch sử"
      portalClassName="ReactModalPortal"
      overlayClassName=""
      className="swing-in-top-fwd modal-history"
    >
      <div className="modal-description">
        <h2>Lịch sử</h2>
        <div className="btn-history">
          <a
            href="#"
            className={`btn-history__item ${tab === "rewards" ? "active-btn" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              setTab("rewards");
            }}
          >
            <span>Lịch sử nhận quà</span>
          </a>
          <a
            href="#"
            className={`btn-history__item ${tab === "predictions" ? "active-btn" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              setTab("predictions");
            }}
          >
            <span>Lịch sử dự đoán</span>
          </a>
          <a
            href="#"
            className={`btn-history__item ${tab === "specials" ? "active-btn" : ""}`}
            onClick={(e) => {
              e.preventDefault();
              setTab("specials");
            }}
          >
            <span>Dự đoán khác</span>
          </a>
        </div>

        <>
          {tab === "rewards" && (
            <div className={style.tableContainer}>
              <table>
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Tên vật phẩm</th>
                    <th>Nhận quà từ</th>
                    <th>Thời gian</th>
                    <th>Trạng thái</th>
                  </tr>
                </thead>
              </table>
              <div className={style.content}>
                <Scrollbar
                  className="modal-history-scroll"
                  style={{ height: "32vw" }}
                  noScrollX
                >
                  {loadingRewards ? (
                    <div className={style.noData}>Đang tải...</div>
                  ) : histories?.length ? (
                    <table className={style.tableScroll}>
                      <tbody>
                        {histories?.map((data, index: number) => {
                          const reward = rewardsMap.get(data?.rewardId);
                          return (
                            <tr key={`${data?.rewardId}-${index}`}>
                              <td>{index + 1}</td>
                              <td>{reward?.name}</td>
                              <td>{getTextUsage(data?.sourceType)}</td>
                              <td>
                                {data?.createdAt
                                  ? dayjs(data.createdAt).format(
                                    "DD/MM/YYYY - HH:mm:ss",
                                  )
                                  : ""}
                              </td>
                              <td>{data?.status ? statusMap[data.status] : ""}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className={style.noData}>Chưa có lịch sử</div>
                  )}
                </Scrollbar>
              </div>
            </div>
          )}

          {tab === "predictions" && (
            <div className={style.tableContainerPredict}>
              <table>
                <thead>
                  <tr>
                    <th>Trận</th>
                    <th>Dự đoán</th>
                    <th>Trên/Dưới</th>
                    <th>Kết quả</th>
                    <th>NSHV</th>
                    <th>Điểm</th>
                    <th>Điểm KQ</th>
                    <th>Điểm O/U</th>
                    <th>Thời gian</th>
                  </tr>
                </thead>
              </table>
              <div className={style.content}>
                <Scrollbar
                  className="modal-history-scroll"
                  style={{ height: "32vw" }}
                  noScrollX
                >
                  {loadingPredicts ? (
                    <div className={style.noData}>Đang tải...</div>
                  ) : hisPredicts?.length > 0 ? (
                    <table className={style.tableScroll}>
                      <tbody>
                        {hisPredicts?.map((predict, index: number) => {
                          const isSettled = predict?.status === "settled";
                          const isCompleted = predict?.match?.processingStatus === "completed";

                          const predictionText =
                            predict?.result === "draw" ? "Hoà" : `${predict?.result} Thắng`;

                          const overUnderLine = predict?.match?.overUnderLine;
                          const overUnderLabel =
                            predict?.overUnder === "over"
                              ? "Trên"
                              : predict?.overUnder === "under"
                                ? "Dưới"
                                : "";
                          const overUnderText = overUnderLabel
                            ? `${overUnderLabel}${typeof overUnderLine === "number" ? ` ${overUnderLine}` : ""}`
                            : "—";

                          const starText = predict?.isUsedHopeStar ? "Có" : "Không";

                          let resultText = "Chưa có";

                          if (
                            isCompleted &&
                            typeof predict?.match?.teamAScore === "number" &&
                            typeof predict?.match?.teamBScore === "number"
                          ) {
                            if (predict?.match?.teamAScore > predict?.match?.teamBScore) {
                              resultText = `${predict?.match?.teamA} Thắng`;
                            } else if (predict?.match?.teamAScore < predict?.match?.teamBScore) {
                              resultText = `${predict?.match?.teamB} Thắng`;
                            } else {
                              resultText = "Hoà";
                            }
                          }

                          const resultRewardText = isSettled
                            ? (predict?.resultRewardPoint ?? 0).toLocaleString()
                            : "—";
                          const overUnderRewardText = isSettled
                            ? (predict?.overUnderRewardPoint ?? 0).toLocaleString()
                            : "—";

                          const createAt = predict?.createdAt;

                          const matchLabel = predict?.match
                            ? `${getTeamCode(predict?.match?.teamA)} vs ${getTeamCode(predict?.match?.teamB)}`
                            : "—";

                          return (
                            <tr key={predict?.matchId ?? index}>
                              <td>{matchLabel}</td>
                              <td>{predictionText}</td>
                              <td>{overUnderText}</td>
                              <td>{resultText}</td>
                              <td>{starText}</td>
                              <td>{(predict?.pointBet ?? 0).toLocaleString()}</td>
                              <td>{resultRewardText}</td>
                              <td>{overUnderRewardText}</td>
                              <td>
                                {createAt
                                  ? dayjs(createAt).format("HH:mm:ss - DD/MM/YYYY")
                                  : "—"}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className={style.noData}>Chưa có lịch sử</div>
                  )}
                </Scrollbar>
              </div>
            </div>
          )}

          {tab === "specials" && (
            <div className={style.tableContainerOther}>
              <table>
                <thead>
                  <tr>
                    <th>Mục dự đoán</th>
                    <th>Dự đoán</th>
                    <th>Trạng thái</th>
                    <th>Điểm nhận được</th>
                    <th>Thời gian</th>
                  </tr>
                </thead>
              </table>
              <div className={style.content}>
                <Scrollbar
                  className="modal-history-scroll"
                  style={{ height: "32vw" }}
                  noScrollX
                >
                  {loadingState ? (
                    <div className={style.noData}>Đang tải...</div>
                  ) : otherPredicts.length ? (
                    <table className={style.tableScroll}>
                      <tbody>
                        {otherPredicts.map((item, index: number) => (
                          <tr key={index}>
                            <td>{item?.type}</td>
                            <td>{item?.name}</td>
                            <td>{item?.statusText}</td>
                            <td>
                              {item?.status === "settled"
                                ? (item?.rewardPoint ?? 0).toLocaleString()
                                : "—"}
                            </td>
                            <td>
                              {item?.createdAt
                                ? dayjs(item?.createdAt).format("HH:mm:ss - DD/MM/YYYY")
                                : "—"}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div className={style.noData}>Chưa có lịch sử</div>
                  )}
                </Scrollbar>
              </div>
            </div>
          )}
        </>
      </div>
      <a href="#" className="close" onClick={selfClose}></a>
    </Modal>
  );
};

export default ModalHistory;
